// Průvodce hovorem podle call scriptu (mockup v8, obrazovky 6 a 7).
// Kroky: 1 Dotazník a předběžný pravidelný čas (nebo Putující), 2 Cíl a zdraví, 3 Termín, 4 E‑mail a faktura, 5 Shrnutí.
// Rozepsaný hovor se průběžně ukládá (koncept), po zavření se nic neztratí.
import React, { useEffect, useRef, useState } from "react";
import { PhoneIncoming, ChevronLeft, ChevronRight, ChevronDown, CheckCircle2, CalendarClock, UserX, Target, HeartPulse, Bandage, Activity, NotebookPen, Users, Building2, Search, CircleCheck, Check, AlertTriangle, History } from "lucide-react";
import { Modal, HlavickaOkna, Field, Segment, Zapinatko } from "../ui.jsx";
import { toast } from "../Toast.jsx";
import { salesGet, salesPost, spustMereniSales, denDlouze, casBezNuly, dm, dowOf, DNY } from "./salesApi.js";
import { CoRict, SkupinaNadpis, Oznameni, Cipy, PoleSIkonou, Fajfky, RadekShrnuti, Pil, CastKroku, Volby } from "./salesUi.jsx";
import TerminPicker from "./TerminPicker.jsx";
import { veDnSlovy } from "./MesicniKalendar.jsx";

const KROKY = ["Dotazník", "Časy a trenér", "Cíl a zdraví", "Termín", "E‑mail a faktura", "Shrnutí"];
const DELKY = ["Zatím neví", "3 měsíce", "6 měsíců", "Rok"];
const FREKVENCE = ["1x", "2x", "3x"];
const DNY_7 = ["", "pondělí", "úterý", "středu", "čtvrtek", "pátek", "sobotu", "neděli"];

function odhadOdpoledne(text) {
  const t = String(text || "").toLowerCase();
  if (/dopol|ráno|rano|dopoledne/.test(t) && !/odpol|večer|vecer/.test(t)) return false;
  return true;
}
function normDelka(t) {
  const s = String(t || "").toLowerCase();
  if (/rok|12/.test(s)) return "Rok";
  if (/6|šest|pul|půl/.test(s)) return "6 měsíců";
  if (/3|tři|tri/.test(s)) return "3 měsíce";
  if (/nev/.test(s)) return "Zatím neví";
  return "";
}
function normFrekvence(t) { const m = String(t || "").match(/[123]/); return m ? `${m[0]}x` : ""; }
function vychoziStav(k) {
  const d = k.dotaznik || {};
  const z = k.zdravi || {};
  const f = k.fakturace || {};
  return {
    krok: 0,
    dotaznik: { kdy_zacit: d.kdy_zacit || "", delka: normDelka(d.delka), frekvence: normFrekvence(d.frekvence), kdy_muze: d.kdy_muze || "" },
    predbezne: { trener_id: "", trener: "", casy: [], putujici: null, ...(k.predbezne || {}) },
    zdravi: { cil: z.cil || "", zdravotni: z.zdravotni || "", zraneni: z.zraneni || "", bolesti: z.bolesti || "", poznamky: z.poznamky || "" },
    termin: null,
    email: k.email || "", emailPotvrzen: false, jmeno: k.jmeno || "",
    fakturace: { typ: f.typ || "osoba", ulice: f.ulice || "", mesto: f.mesto || "", psc: f.psc || "", firma: f.firma || "", ico: f.ico || "", dic: f.dic || "" },
  };
}

function Kroky({ akt }) {
  return (
    <div className="mb-4 space-y-1.5">
      <div className="grid grid-cols-6 gap-1.5">
        {KROKY.map((l, i) => (
          <div key={l} className="min-w-0 space-y-1.5">
            <div className={`h-1.5 rounded-full ${i < akt ? "bg-brand-400" : i === akt ? "bg-brand" : "bg-slate-200"}`} />
            <div className={`hidden truncate text-[12px] font-bold lg:block ${i === akt ? "text-brand-deep" : "text-slate-500"}`}>{i + 1}. {l}</div>
          </div>
        ))}
      </div>
      <div className="text-[13px] font-bold text-brand-deep lg:hidden">Krok {akt + 1} z 6: {KROKY[akt]}</div>
    </div>
  );
}

function Preruseni({ onJindy, onNezajem }) {
  return (
    <div className="mt-6 space-y-3 border-t border-slate-200 pt-4">
      <div className="text-[16px] font-bold text-slate-900">Hovor skončil jinak?</div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn-secondary !min-h-[52px]" onClick={onJindy}><CalendarClock className="h-4 w-4" />Zavolat jindy</button>
        <button type="button" className="btn border border-rose-300 bg-white text-rose-700 !min-h-[52px]" onClick={onNezajem}><UserX className="h-4 w-4" />Nemá zájem</button>
      </div>
    </div>
  );
}

// Předběžně nabídnuté pravidelné tréninky (1. 10. 2026, mockup „Pravidelné časy v hovoru“, varianta A).
// Nabízí jen časy, které projdou kontrolami na serveru (salesPravidelne.cjs): jiné klientky,
// navazující tréninky, série, Dostupnost a místo pro putující. Skryté časy jsou pod
// „Proč tu nejsou další časy“, ať Miluše ví, proč třeba út 17:00 nejde nabídnout.
const TON_DUVODU = { problem: "problem", pozor: "pozor", neutralni: "neutralni" };

function SkryteCasy({ skryte }) {
  const [otevreno, setOtevreno] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (otevreno && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [otevreno]);
  if (!skryte?.length) return null;
  return (
    <div className="rounded-xl bg-slate-50">
      <button type="button" onClick={() => setOtevreno((o) => !o)} aria-expanded={otevreno}
        className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-[13px] font-bold text-brand-deep">
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
        <span className="min-w-0 flex-1">Proč tu nejsou další časy</span>
        <span className="rounded-full bg-white px-2 py-0.5 text-[12px] tabular-nums text-slate-600">{skryte.length}</span>
      </button>
      {otevreno && (
        <div ref={ref} className="scroll-mt-24 divide-y divide-slate-200 px-3 pb-2">
          {skryte.map((r) => (
            <div key={`${r.popis}-${r.stitek}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2">
              <span className="rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-[13px] font-bold tabular-nums text-slate-400 line-through">{r.popis}</span>
              <Pil ton={TON_DUVODU[r.ton] || "neutralni"} casti={r.stitek} mala />
              <span className="w-full text-[13px] leading-snug text-slate-500">{r.vysv}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 2. 10. 2026: stejný výběr používá i Návrat klientky (NavratDialog), proto volitelné texty,
// lokalita a výchozí trenér (u návratu ten, u kterého byla).
export function PredbeznaNabidka({ stav, setStav, cislo = 2, nazev = "Nabídni pravidelný čas", popisZdroje = "Podle dotazníku:", lokalita = null, vychoziTrenerId = null, bezVolnaText = null, povinne = false, leadId = null, onJindy = null }) {
  const d = stav.dotaznik;
  const castZDotazniku = odhadOdpoledne(d.kdy_muze) ? "odpoledne" : "dopoledne";
  const [cast, setCast] = useState(() => stav.predbezne.putujici?.okno || castZDotazniku);
  const [data, setData] = useState(null);
  const [nacitam, setNacitam] = useState(false);
  const [chyba, setChyba] = useState("");
  const [zmizelo, setZmizelo] = useState("");
  const frekvence = Number(String(d.frekvence || "").replace(/\D/g, "")) || 0;
  const max = frekvence || 3;
  const vybrany = stav.predbezne;
  const casy = vybrany.casy || [];
  const vybranoParam = vybrany.trener_id && casy.length ? `${vybrany.trener_id}|${casy.map((c) => `${c.dow}-${c.hod}`).join(",")}` : "";

  useEffect(() => {
    let zr = false;
    setNacitam(true); setChyba("");
    const t = setTimeout(() => {
      salesGet("pravidelne", { lokalita, odpoledne: cast === "odpoledne" ? "1" : "0", frekvence: frekvence || 1, vybrano: vybranoParam }).then((r) => {
        if (zr) return;
        setData(r);
        // Vybraný čas, který mezitím někdo obsadil, z výběru vypadne a Miluše to uvidí.
        const tr = r.treneri.find((x) => String(x.id) === String(vybrany.trener_id));
        const pryc = casy.filter((c) => !(tr?.volno || []).some((v) => v.dow === c.dow && v.hod === c.hod));
        if (pryc.length) {
          setZmizelo(`${pryc.map((c) => c.popis).join(" a ")} už nejde nabídnout, mezitím se obsadil.`);
          setStav((s) => ({ ...s, predbezne: { ...s.predbezne, casy: (s.predbezne.casy || []).filter((c) => !pryc.some((p) => p.dow === c.dow && p.hod === c.hod)) } }));
        }
        if (!vybrany.trener_id) {
          const prior = (vychoziTrenerId && r.treneri.find((x) => String(x.id) === String(vychoziTrenerId) && !x.plno)) || r.treneri.find((x) => x.prioritni && !x.plno) || null;
          if (prior) setStav((s) => ({ ...s, predbezne: { trener_id: prior.id, trener: prior.jmeno, casy: [], putujici: null } }));
        }
      }).catch((e) => !zr && setChyba(e.message)).finally(() => !zr && setNacitam(false));
    }, 150);
    return () => { zr = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cast, vybranoParam, frekvence, lokalita]);

  // 20. 10. 2026 (Láďa): kapacita. Když nikdo nemá volno ani místo pro putující dopoledne ani
  // odpoledne, nabírání se pozastaví (zkušební se nedomlouvá) a Láďovi přijde zpráva od Jokyho.
  // Když se časy nenačtou kvůli chybě, domluví se jen zkušební, zbytek trenér na tréninku.
  const maMisto = (r) => (r?.treneri || []).some((t) => (t.volno || []).length || t.putujici?.mozno);
  const [druhaMaMisto, setDruhaMaMisto] = useState(null);
  const nahlaseno = React.useRef({});
  const nahlas = (typ, detail) => {
    if (!leadId || nahlaseno.current[typ]) return;
    nahlaseno.current[typ] = true;
    salesPost("kapacita_hlaseni", { id: leadId, typ, detail }).catch(() => {});
  };
  useEffect(() => {
    setDruhaMaMisto(null);
    if (!povinne || !data || maMisto(data)) return;
    let zr = false;
    salesGet("pravidelne", { lokalita, odpoledne: cast === "odpoledne" ? "0" : "1", frekvence: frekvence || 1 })
      .then((r) => { if (!zr) setDruhaMaMisto(maMisto(r)); })
      .catch(() => { if (!zr) setDruhaMaMisto(null); });
    return () => { zr = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, povinne]);
  const plno = povinne && !!data && !maMisto(data) && druhaMaMisto === false;
  useEffect(() => {
    if (!plno) return;
    nahlas("plno");
    // Při plné kapacitě nesmí zůstat žádný výběr (ani z rozepsaného hovoru), jinak by šlo pokračovat.
    setStav((st) => ({ ...st, predbezne: { trener_id: "", trener: "", casy: [], putujici: null, bez: false } }));
  }, [plno]); // eslint-disable-line react-hooks/exhaustive-deps
  // „Jen se zkušebním“ platí jen při chybě načtení. Když se časy načtou, volba se zruší.
  useEffect(() => {
    if (data && !chyba && vybrany.bez) setStav((st) => ({ ...st, predbezne: { ...st.predbezne, bez: false } }));
  }, [data, chyba]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (povinne && chyba) nahlas("chyba", chyba); }, [povinne, chyba]); // eslint-disable-line react-hooks/exhaustive-deps

  const zmenCast = (c) => {
    setCast(c); setZmizelo("");
    setStav((s) => ({ ...s, predbezne: { ...s.predbezne, casy: [], putujici: null, bez: false } }));
  };
  const vyberTrenera = (t, zap) => setStav((s) => ({ ...s, predbezne: zap ? { trener_id: "", trener: "", casy: [], putujici: null } : { trener_id: t.id, trener: t.jmeno, casy: [], putujici: null } }));
  const prepniCas = (t, c) => { setZmizelo(""); setStav((s) => {
    const stejny = String(s.predbezne.trener_id) === String(t.id);
    const puvodni = stejny ? (s.predbezne.casy || []) : [];
    const ma = puvodni.some((x) => x.dow === c.dow && x.hod === c.hod);
    // Každý trénink v jiný den (1. 10. 2026): druhý čas ve stejný den nahradí ten první.
    const nove = ma ? puvodni.filter((x) => !(x.dow === c.dow && x.hod === c.hod)) : [...puvodni.filter((x) => x.dow !== c.dow), { dow: c.dow, hod: c.hod, popis: c.popis }].slice(-max);
    return { ...s, predbezne: { trener_id: t.id, trener: t.jmeno, casy: nove.sort((a, b) => a.dow - b.dow || a.hod - b.hod), putujici: null } };
  }); };
  const prepniPutujici = (t) => setStav((s) => {
    const uz = String(s.predbezne.trener_id) === String(t.id) && s.predbezne.putujici;
    return { ...s, predbezne: { trener_id: t.id, trener: t.jmeno, casy: [], putujici: uz ? null : { okno: cast, frekvence: frekvence || 1 } } };
  });

  const casyText = casy.map((c) => `${DNY_7[c.dow]} v ${c.hod}:00`).join(" a ");
  const krestni = String(vybrany.trener || "").split(" ")[0];
  const trenerka = /a$/i.test(krestni);
  return (
    <CastKroku cislo={cislo} nazev={nazev}>
      <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-slate-600">
        <span>{popisZdroje}</span>
        <Pil ton="znacka" casti={frekvence ? `${frekvence}x týdně` : "kolikrát týdně zatím neví"} mala />
        <Pil ton="znacka" casti={castZDotazniku} mala />
        <span>{frekvence ? `vyber ${frekvence === 1 ? "1 čas" : `${frekvence} časy v různých dnech`} u jednoho trenéra, nebo Putující` : "vyber až 3 časy v různých dnech u jednoho trenéra, nebo Putující"}</span>
      </div>
      <CoRict>
        {vybrany.putujici
          ? <>Mohu vám předběžně nabídnout tréninky u {trenerka ? "trenérky" : "trenéra"} <b>{krestni}</b> <b>{vybrany.putujici.okno}, bez pevného času</b>. Termíny si s {trenerka ? "ní" : "ním"} domluvíte vždy na další týdny dopředu. Vyhovovalo by vám to?</>
          : casy.length
            ? <>Mohu vám předběžně nabídnout tréninky u {trenerka ? "trenérky" : "trenéra"} <b>{krestni}</b> v <b>{casyText}</b>, vyhovovalo by vám to? Skvěle, poznamenám si to, ať to nikomu dalšímu nenabídnu.</>
            : <>Mohu vám předběžně nabídnout pravidelné tréninky u trenéra, který má volno v časech, kdy můžete.</>}
      </CoRict>
      <Segment aria-label="Část dne" value={cast} onChange={zmenCast} className="max-w-xs"
        items={[{ key: "dopoledne", label: "Dopoledne" }, { key: "odpoledne", label: "Odpoledne" }]} />
      {chyba && !povinne && <Oznameni ton="problem">{chyba}</Oznameni>}
      {zmizelo && <Oznameni ton="pozor">{zmizelo}</Oznameni>}
      {!data && !chyba && <div className="h-24 animate-pulse rounded-xl bg-brand-light" />}
      {data && !data.treneri.length && !plno && <Oznameni ton="info">Teď nikdo nemá {cast} volno. {bezVolnaText || (povinne ? "Zkus druhou část dne." : "Předběžnou nabídku můžeš vynechat a domluvit jen zkušební.")}</Oznameni>}
      {povinne && data && !maMisto(data) && druhaMaMisto === true && (
        <div className="space-y-2">
          <Oznameni ton="info">{cast === "dopoledne" ? "Dopoledne" : "Odpoledne"} nikdo nemá volno ani místo pro putující. {cast === "dopoledne" ? "Odpoledne" : "Dopoledne"} místo je, zeptej se, jestli by jí to šlo.</Oznameni>
          <button type="button" className="btn-secondary w-full !min-h-[52px] sm:w-auto sm:!min-h-[44px]" onClick={() => zmenCast(cast === "dopoledne" ? "odpoledne" : "dopoledne")}>Ukázat {cast === "dopoledne" ? "odpoledne" : "dopoledne"}</button>
        </div>
      )}
      {plno && (
        <div className="space-y-2">
          <Oznameni ton="problem"><b>Nabírání je pozastavené.</b> Teď nikdo nemá volno ani místo pro putující, dopoledne ani odpoledne. Zkušební trénink nedomlouvej, naplánuj jí Zavolat jindy. Láďovi přišla zpráva.</Oznameni>
          {onJindy && <button type="button" className="btn-primary w-full !min-h-[52px] sm:w-auto sm:!min-h-[44px]" onClick={onJindy}><CalendarClock className="h-4 w-4" />Zavolat jindy</button>}
        </div>
      )}
      {/* Chyba při načtení: domluví se jen zkušební, pravidelné časy trenér na tréninku. */}
      {povinne && chyba && (
        <div className="space-y-2">
          <Oznameni ton="pozor"><b>Došlo k chybě systému, volné časy se nenačetly.</b> Domluv jen zkušební trénink, pravidelné časy domluví trenér přímo na tréninku. Dej prosím vědět Láďovi, zprávu mu poslal i Joky.</Oznameni>
          <button type="button" className={`btn-secondary w-full !min-h-[52px] sm:w-auto sm:!min-h-[44px] ${vybrany.bez ? "!border-brand !bg-brand-50" : ""}`} aria-pressed={!!vybrany.bez}
            onClick={() => setStav((s) => ({ ...s, predbezne: { trener_id: "", trener: "", casy: [], putujici: null, bez: !s.predbezne.bez } }))}>
            {vybrany.bez ? "Pokračuje se jen se zkušebním" : "Pokračovat jen se zkušebním"}
          </button>
        </div>
      )}
      <div className={`grid gap-2 lg:grid-cols-3 ${nacitam && data ? "opacity-70" : ""}`} aria-busy={nacitam}>
        {(data?.treneri || []).map((t) => {
          const zap = String(vybrany.trener_id) === String(t.id);
          const putZap = zap && !!vybrany.putujici;
          const pozor = zap ? t.volno.find((c) => c.pozor && casy.some((x) => x.dow === c.dow && x.hod === c.hod)) : null;
          return (
            <div key={t.id} className={`space-y-2.5 rounded-xl border p-3 ${zap ? "border-2 border-brand bg-brand-50" : t.plno ? "border-slate-200 bg-slate-50" : t.prioritni ? "border-brand-gold bg-brand-gold/10" : "border-slate-200 bg-white"}`}>
              <button type="button" className="flex w-full min-h-[44px] items-center justify-between gap-2 text-left" onClick={() => vyberTrenera(t, zap)} aria-pressed={zap}>
                <span className="flex flex-wrap items-center gap-1.5 text-[15px] font-bold text-slate-900">{t.jmeno}{t.prioritni && <Pil ton="zlata" casti="přednostně" mala />}</span>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${zap ? "border-brand bg-brand text-white" : "border-slate-300"}`}>{zap && <Check className="h-3.5 w-3.5" />}</span>
              </button>
              {t.plno && <div className="rounded-lg bg-white px-2.5 py-2 text-[13px] leading-snug text-slate-600">{t.plnoText}</div>}
              {!!t.volno.length && (
                <div className="flex flex-wrap gap-1.5">
                  {t.volno.map((c) => {
                    const on = zap && casy.some((x) => x.dow === c.dow && x.hod === c.hod);
                    return (
                      <button key={`${c.dow}-${c.hod}`} type="button" className="cip tabular-nums" aria-pressed={on} onClick={() => prepniCas(t, c)}
                        title={c.pozor || undefined}>
                        {c.pozor && <span aria-hidden className={`mr-1.5 inline-block h-2 w-2 rounded-full ${on ? "bg-white" : "bg-amber-500"}`} />}
                        {c.popis}
                      </button>
                    );
                  })}
                </div>
              )}
              {pozor && <Oznameni ton="pozor">{pozor.popis}: {pozor.pozor}</Oznameni>}
              <button type="button" onClick={() => prepniPutujici(t)} disabled={!t.putujici.mozno && !putZap} aria-pressed={putZap}
                className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-[14px] font-bold disabled:cursor-not-allowed disabled:opacity-55 ${putZap ? "border-brand bg-brand-100 text-brand-deep" : "border-dashed border-brand-300 bg-white text-brand-deep"}`}>
                <span className="min-w-0">
                  <span className="block">Putující, {cast}</span>
                  <span className="block text-[12px] font-medium text-slate-500">{t.putujici.text}</span>
                </span>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${putZap ? "border-brand bg-brand" : "border-slate-300"}`}>{putZap && <span className="h-2 w-2 rounded-full bg-white" />}</span>
              </button>
              <SkryteCasy skryte={t.skryte} />
            </div>
          );
        })}
      </div>
    </CastKroku>
  );
}

export default function HovorPruvodce({ kontakt, open, onClose, onJindy, onNezajem, onHotovo }) {
  const [stav, setStav] = useState(() => vychoziStav(kontakt));
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const [vysledek, setVysledek] = useState(null);
  const [aresBusy, setAresBusy] = useState(false);
  const [aresOk, setAresOk] = useState(false);
  const telo = useRef(null);

  // Otevření: zapsat, že zvedla, případně spustit měření a obnovit rozepsaný hovor.
  useEffect(() => {
    if (!open) return;
    setChyba(""); setVysledek(null); setAresOk(false);
    const k = kontakt.koncept;
    // 20. 10. 2026: krok „Časy a trenér“ se oddělil od dotazníku (6 kroků místo 5). Koncept uložený
    // před změnou (bez verze) má staré číslování (0 Dotazník s časy, 1 Cíl a zdraví, 2 Termín…).
    // Krok 0 zůstává, starý krok 1 vede na nový krok Časy a trenér (výběr se zkontroluje a pak
    // pokračuje na Cíl a zdraví), od starého kroku 2 výš se posouvá o jeden.
    const krokZKonceptu = (kk) => {
      const n = Math.max(0, Number(kk && kk.krok) || 0);
      return Math.min((kk && kk.verze >= 2) || n <= 1 ? n : n + 1, 5);
    };
    setStav(k && k.jmeno ? { ...vychoziStav(kontakt), ...k, krok: krokZKonceptu(k), verze: 2, emailPotvrzen: false } : vychoziStav(kontakt));
    salesPost("zvedla", { id: kontakt.id }).catch(() => {});
    spustMereniSales().then((spusteno) => spusteno && toast("Měření Sales spuštěno", "success"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kontakt.id]);

  // Průběžné uložení konceptu (1,5 s po poslední změně).
  useEffect(() => {
    if (!open || vysledek) return undefined;
    const t = setTimeout(() => { salesPost("koncept", { id: kontakt.id, koncept: { ...stav, verze: 2, emailPotvrzen: false } }).catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, [stav, open, kontakt.id, vysledek]);

  useEffect(() => { telo.current?.closest(".overflow-y-auto")?.scrollTo?.({ top: 0 }); }, [stav.krok]);

  const nast = (cesta, hodnota) => setStav((s) => {
    const [a, b] = cesta.split(".");
    return b ? { ...s, [a]: { ...s[a], [b]: hodnota } } : { ...s, [a]: hodnota };
  });
  const d = stav.dotaznik;
  const f = stav.fakturace;
  const t = stav.termin;

  // 16. 10. 2026 (Láďa, schválený mockup): kontakt, který už zkušební měl, sales volí cenu.
  const [drivejsiZnovu, setDrivejsiZnovu] = useState(null);
  const drivejsi = drivejsiZnovu || kontakt.drivejsi_zkusebni || [];
  const opakovana = drivejsi.length > 0;
  // 19. 10. 2026 (Láďa): přechází k jinému trenérovi, zkušební je automaticky za zvýhodněnou cenu.
  const [poZkZnovu, setPoZkZnovu] = useState(undefined);
  const poZk = poZkZnovu !== undefined ? poZkZnovu : kontakt.po_zkusebnim;
  const jinyTrener = opakovana && poZk?.typ === "jiny_trener" ? poZk : null;
  const volbaCeny = opakovana && !jinyTrener;
  const kc = (n) => `${Number(n).toLocaleString("cs-CZ")} Kč`;
  const cenaNizka = kc(kontakt.ceny_zkusebni?.zvyhodnena || 490);
  const cenaPlna = kc(kontakt.ceny_zkusebni?.plna || 1000);
  const vybranoPravidelne = !!((stav.predbezne.trener_id && ((stav.predbezne.casy || []).length || stav.predbezne.putujici)) || stav.predbezne.bez);
  const kontrolaKroku = () => {
    if (stav.krok === 1 && !vybranoPravidelne) return "Vyber pravidelné časy u jednoho trenéra, nebo Putující.";
    if (stav.krok === 3 && !t) return "Vyber termín zkušebního tréninku.";
    if (stav.krok === 4) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(stav.email.trim())) return "Zkontroluj e‑mail.";
      if (!stav.emailPotvrzen) return "Přečti e‑mail celý nahlas a přepni, že ho klientka potvrdila.";
      if (stav.jmeno.trim().split(/\s+/).length < 2) return "Jméno do systému: jedno jméno a jedno příjmení.";
      if (f.typ === "firma" ? !(f.ico && f.firma) : !(f.ulice && f.mesto && f.psc)) return f.typ === "firma" ? "Doplň IČO a načti firmu." : "Doplň fakturační adresu.";
      if (volbaCeny && !["490", "1000"].includes(stav.cena || "")) return "Vyber cenu zkušebního tréninku, už ho u nás měla.";
      if (volbaCeny && stav.cena === "490" && !String(stav.cenaProc || "").trim()) return "Napiš, proč dáváš zvýhodněnou cenu.";
    }
    return "";
  };
  const dal = () => { const c = kontrolaKroku(); if (c) { setChyba(c); return; } setChyba(""); nast("krok", Math.min(5, stav.krok + 1)); };
  const zpet = () => { setChyba(""); nast("krok", Math.max(0, stav.krok - 1)); };

  const nactiAres = async () => {
    setAresBusy(true); setChyba(""); setAresOk(false);
    try {
      const a = await salesGet("ares", { ico: f.ico });
      setStav((s) => ({ ...s, fakturace: { ...s.fakturace, ico: a.ico, firma: a.firma, dic: a.dic || "", ulice: a.ulice, mesto: a.mesto, psc: a.psc } }));
      setAresOk(true);
    } catch (e) { setChyba(e.message); }
    finally { setAresBusy(false); }
  };

  const domluvit = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("domluvit", {
        id: kontakt.id, jmeno: stav.jmeno, email: stav.email, emailPotvrzen: stav.emailPotvrzen,
        dotaznik: d, zdravi: stav.zdravi, predbezne: stav.predbezne.trener_id && ((stav.predbezne.casy || []).length || stav.predbezne.putujici) ? stav.predbezne : null,
        termin: t, fakturace: f,
        ...(volbaCeny ? { cena: stav.cena, cenaProc: stav.cenaProc || "" } : {}),
      });
      setVysledek(r);
      onHotovo?.(r);
    } catch (e) {
      setChyba(e.message);
      // Karta byla otevřená dřív, než vznikl předchozí zkušební: načíst znovu a ukázat výběr ceny.
      if (/cenu zkušebního/i.test(e.message)) {
        try { const k = await salesGet("kontakt", { id: kontakt.id }); setDrivejsiZnovu(k.drivejsi_zkusebni || []); setPoZkZnovu(k.po_zkusebnim || null); } catch { /* zůstane hláška */ }
        nast("krok", 4);
      } else if (/termín|obsaz|souběh|trénink/i.test(e.message)) nast("krok", 3);
    } finally { setBusy(false); }
  };

  const kdyText = t ? `${DNY[dowOf(t.datum)]} ${dm(t.datum)} v ${casBezNuly(t.cas)}` : "";
  const pravidelne = [d.frekvence ? `${d.frekvence} týdně` : "", stav.predbezne.putujici ? `putující, ${stav.predbezne.putujici.okno}` : (stav.predbezne.casy || []).map((c) => c.popis).join(" a ")].filter(Boolean).join(", ");

  const patka = vysledek
    ? <button type="button" className="btn-primary" onClick={onClose}>Zpět na Plán</button>
    : stav.krok === 5
      ? <>
          <button type="button" className="btn-secondary" onClick={zpet}><ChevronLeft className="h-4 w-4" />Zpět</button>
          <button type="button" className="btn bg-emerald-600 text-white hover:bg-emerald-700" onClick={domluvit} disabled={busy} aria-busy={busy}><CircleCheck className="h-4 w-4" />{busy ? "Domlouvám…" : "Domluvit"}</button>
        </>
      : <>
          <button type="button" className="btn-secondary" onClick={stav.krok === 0 ? onClose : zpet}><ChevronLeft className="h-4 w-4" />{stav.krok === 0 ? "Zavřít" : "Zpět"}</button>
          <button type="button" className="btn-primary disabled:opacity-50" onClick={dal} disabled={stav.krok === 1 && !vybranoPravidelne}>Pokračovat<ChevronRight className="h-4 w-4" /></button>
        </>;

  const preruseni = <Preruseni onJindy={onJindy} onNezajem={onNezajem} />;
  const dotaznikText = [
    d.kdy_zacit && <>chcete začít <b>{d.kdy_zacit.toLowerCase()}</b></>,
    d.delka && (d.delka === "Zatím neví" ? <>ještě <b>nevíte, na jak dlouho</b></> : <>na <b>{d.delka.toLowerCase()}</b></>),
    d.frekvence && <><b>{d.frekvence} týdně</b></>,
    d.kdy_muze && <>můžete <b>{d.kdy_muze.toLowerCase()}</b></>,
  ].filter(Boolean);

  return (
    <Modal open={open} onClose={onClose} size="lg" plne plnaHlavicka
      header={<HlavickaOkna ikona={PhoneIncoming} nazev="Hovor" popis={`${kontakt.jmeno}${kontakt.telefon ? `, ${kontakt.telefon}` : ""}`} onClose={onClose} />}
      footer={patka}>
      <div ref={telo}>
        {vysledek ? (
          <div className="space-y-4 py-2 text-center">
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50"><CheckCircle2 className="h-10 w-10 text-emerald-600" /></span>
            <div className="font-serif text-2xl font-bold text-slate-900">Domluveno</div>
            <div className="text-[16px] text-slate-600"><b className="text-slate-900">{stav.jmeno}</b>, <b className="text-slate-900">{denDlouze(vysledek.datum)} v {casBezNuly(vysledek.cas)}</b> <b className="text-slate-900">{vysledek.trener_7}</b></div>
            <div className="mx-auto max-w-md space-y-2 rounded-2xl border border-slate-200 bg-white p-4 text-left">
              {(vysledek.kroky || []).filter((k) => k.text).map((k, i) => (
                <div key={i} className="flex items-start gap-2.5 text-[15px] text-slate-800">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${k.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{k.ok ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}</span>
                  <span>{k.text}{!k.ok && k.chyba ? <span className="block text-[13px] text-rose-700">{k.chyba}</span> : null}</span>
                </div>
              ))}
            </div>
            {(vysledek.kroky || []).some((k) => !k.ok) && <Oznameni ton="pozor">Něco neprošlo, trénink je ale zapsaný. Napiš prosím Láďovi, co je červeně.</Oznameni>}
          </div>
        ) : (
          <>
            <Kroky akt={stav.krok} />
            {chyba && <Oznameni ton="problem" className="mb-3">{chyba}</Oznameni>}

            {stav.krok === 0 && (
              <div className="space-y-4">
                <CastKroku cislo={1} nazev="Ověř dotazník" hotovo={!!(d.frekvence && d.kdy_muze)}>
                <CoRict>
                  {dotaznikText.length
                    ? <>Skvěle, nejprve si ověřím vaše odpovědi. Vyplnila jste, že {dotaznikText.map((x, i) => <React.Fragment key={i}>{i > 0 && (i === dotaznikText.length - 1 ? " a " : ", ")}{x}</React.Fragment>)}, souhlasí to?</>
                    : <>Skvěle, nejprve se vás zeptám na pár věcí. Kdy byste chtěla začít, na jak dlouho, kolikrát týdně a kdy můžete trénovat?</>}
                </CoRict>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Kdy chce začít"><input className="field" value={d.kdy_zacit} onChange={(e) => nast("dotaznik.kdy_zacit", e.target.value)} placeholder="Např. hned, od 12. 10." /></Field>
                  <div><span className="label">Délka spolupráce</span><Cipy moznosti={DELKY} value={d.delka} onChange={(v) => nast("dotaznik.delka", v)} /></div>
                  <div><span className="label">Kolikrát týdně</span><Cipy moznosti={FREKVENCE} value={d.frekvence} onChange={(v) => nast("dotaznik.frekvence", v)} /></div>
                  <Field label="Kdy může trénovat"><input className="field" value={d.kdy_muze} onChange={(e) => nast("dotaznik.kdy_muze", e.target.value)} placeholder="Např. odpoledne po 17h, út a čt" /></Field>
                </div>
                </CastKroku>
                {preruseni}
              </div>
            )}

            {/* 20. 10. 2026 (Láďa): pravidelné časy a trenér jako samostatný povinný krok, dřív byly
                pod dotazníkem a při rolování se na ně zapomínalo. */}
            {stav.krok === 1 && (
              <div className="space-y-4">
                <PredbeznaNabidka stav={stav} setStav={setStav} cislo={1} nazev="Vyber pravidelné časy a trenéra" povinne leadId={kontakt?.id} onJindy={onJindy} />
                {!vybranoPravidelne && <Oznameni ton="pozor"><b>Bez výběru nejde pokračovat.</b> Klepni na časy u jednoho trenéra, nebo vyber Putující.</Oznameni>}
                {preruseni}
              </div>
            )}

            {stav.krok === 2 && (
              <div className="space-y-4">
                <CoRict>Co je vaším cílem, proč chcete začít cvičit? Máte nějaké zdravotní omezení? Zranění z minulosti? Chronické bolesti?</CoRict>
                <Oznameni ton="info">Tohle uvidí trenér v kalendáři a na kartě klientky. Klientce se to nikde neukazuje.</Oznameni>
                <div className="grid gap-4 lg:grid-cols-2">
                  <PoleSIkonou popisek="Cíl" ikona={Target}><textarea className="field min-h-[64px]" value={stav.zdravi.cil} onChange={(e) => nast("zdravi.cil", e.target.value)} /></PoleSIkonou>
                  <PoleSIkonou popisek="Zdravotní omezení" ikona={HeartPulse}><textarea className="field min-h-[64px]" value={stav.zdravi.zdravotni} onChange={(e) => nast("zdravi.zdravotni", e.target.value)} placeholder="Např. tlak, štítná žláza" /></PoleSIkonou>
                  <PoleSIkonou popisek="Zranění z minulosti" ikona={Bandage}><textarea className="field min-h-[64px]" value={stav.zdravi.zraneni} onChange={(e) => nast("zdravi.zraneni", e.target.value)} /></PoleSIkonou>
                  <PoleSIkonou popisek="Chronické bolesti" ikona={Activity}><textarea className="field min-h-[64px]" value={stav.zdravi.bolesti} onChange={(e) => nast("zdravi.bolesti", e.target.value)} /></PoleSIkonou>
                </div>
                <PoleSIkonou popisek="Poznámky: práce, spánek, psychika, strava" ikona={NotebookPen}><textarea className="field min-h-[64px]" value={stav.zdravi.poznamky} onChange={(e) => nast("zdravi.poznamky", e.target.value)} /></PoleSIkonou>
                {preruseni}
              </div>
            )}

            {stav.krok === 3 && (
              <div className="space-y-3">
                <CoRict>{t ? <>Teď si domluvíme termín zkušebního tréninku. Jak to máte <b>{veDnSlovy(t.datum)}</b>, vyhovovalo by vám <b>{casBezNuly(t.cas)}</b>?</> : <>Teď si domluvíme termín zkušebního tréninku. Který den a čas by vám vyhovoval?</>}</CoRict>
                <Oznameni ton="znacka">Kalendář ukazuje celý měsíc, šipkou vpravo přepneš na další. Předvybraný je první volný den od příštího týdne.{stav.predbezne.trener_id ? " Filtr Trenér je předvyplněný trenérem z 2. kroku, jde změnit na Všichni." : ""}</Oznameni>
                <TerminPicker trenerVychozi={stav.predbezne.trener_id ? String(stav.predbezne.trener_id) : ""} value={t} onChange={(v) => { nast("termin", v); setChyba(""); }} vychoziOdpoledne={odhadOdpoledne(d.kdy_muze)} />
                {preruseni}
              </div>
            )}

            {stav.krok === 4 && (
              <div className="space-y-4">
                <CoRict>Na váš e‑mail <b>{stav.email || "…"}</b> vám pošlu všechny informace, ceník a fakturu, která potvrzuje termín. Můžete mi prosím říct vaši fakturační adresu?</CoRict>
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-4">
                    <SkupinaNadpis>E‑mail, přečti ho celý nahlas</SkupinaNadpis>
                    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-lg font-bold tracking-wide text-slate-900 [overflow-wrap:anywhere]">{stav.email || "e‑mail chybí"}</div>
                    <Field label="Opravit e‑mail"><input className="field" inputMode="email" value={stav.email} onChange={(e) => { nast("email", e.target.value.trim()); nast("emailPotvrzen", false); }} /></Field>
                    <Zapinatko zapnuto={stav.emailPotvrzen} onZmena={(v) => nast("emailPotvrzen", v)} popisek="Klientka e‑mail potvrdila" ikona={CircleCheck} />
                    <Field label="Jméno a příjmení do systému" hint="Jedno jméno a jedno příjmení, s háčky a čárkami.">
                      <input className="field" value={stav.jmeno} onChange={(e) => nast("jmeno", e.target.value)} />
                    </Field>
                  </div>
                  <div className="space-y-4">
                    <SkupinaNadpis>Fakturační údaje</SkupinaNadpis>
                    <Segment items={[{ key: "osoba", label: <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" />Na osobu</span> }, { key: "firma", label: <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4" />Na firmu</span> }]} value={f.typ} onChange={(v) => nast("fakturace.typ", v)} aria-label="Fakturace" />
                    {f.typ === "firma" && (
                      <>
                        <div>
                          <span className="label">IČO</span>
                          <div className="flex gap-2">
                            <input className="field flex-1" inputMode="numeric" value={f.ico} onChange={(e) => { nast("fakturace.ico", e.target.value.replace(/\D/g, "").slice(0, 8)); setAresOk(false); }} />
                            <button type="button" className="btn-secondary shrink-0" onClick={nactiAres} disabled={aresBusy || f.ico.length < 6} aria-busy={aresBusy}><Search className="h-4 w-4" />Načíst</button>
                          </div>
                        </div>
                        {aresOk && <Oznameni ton="ok">Načteno z registru firem (ARES), zkontroluj prosím.</Oznameni>}
                        <Field label="Firma"><input className="field" value={f.firma} onChange={(e) => nast("fakturace.firma", e.target.value)} /></Field>
                      </>
                    )}
                    <Field label="Ulice a číslo"><input className="field" value={f.ulice} onChange={(e) => nast("fakturace.ulice", e.target.value)} autoComplete="off" /></Field>
                    <div className="grid grid-cols-[1fr_7.5rem] gap-3">
                      <Field label="Město"><input className="field" value={f.mesto} onChange={(e) => nast("fakturace.mesto", e.target.value)} /></Field>
                      <Field label="PSČ"><input className="field" inputMode="numeric" value={f.psc} onChange={(e) => nast("fakturace.psc", e.target.value)} /></Field>
                    </div>
                    {f.typ === "firma" && <Field label="DIČ"><input className="field" value={f.dic} onChange={(e) => nast("fakturace.dic", e.target.value)} /></Field>}
                  </div>
                </div>
                {jinyTrener && (
                  <div className="space-y-3">
                    <SkupinaNadpis>Cena zkušebního tréninku</SkupinaNadpis>
                    <Oznameni ton="ok" ikona={History}>
                      <b>Přechází k jinému trenérovi, zkušební je za {cenaNizka}.</b> Minulý zkušební: trenér {jinyTrener.trener}{jinyTrener.zkusebni ? `, ${dm(jinyTrener.zkusebni)}` : "."} Cenu nevybíráš, nastaví se sama.
                    </Oznameni>
                    <CoRict>Zkušební trénink u nového trenéra vám můžeme nabídnout znovu za zvýhodněnou cenu <b>{cenaNizka}</b>. Fakturu vám pošlu e‑mailem spolu s ostatními informacemi.</CoRict>
                  </div>
                )}
                {volbaCeny && (
                  <div className="space-y-3">
                    <SkupinaNadpis>Cena zkušebního tréninku</SkupinaNadpis>
                    <Oznameni ton="pozor" ikona={History}>
                      <b>Zkušební trénink už u nás měla.</b>
                      <ul className="mt-1 space-y-0.5">
                        {drivejsi.map((z, i) => <li key={i}><b>{z.datum}</b>{[z.trener, z.misto].filter(Boolean).map((x) => `, ${x}`).join("")}: {z.vysledek}</li>)}
                      </ul>
                      <div className="mt-1.5">Rozhodni, za kolik jí ho nabídneš. Zvýhodněná cena {cenaNizka} je pro první zkušební trénink.</div>
                    </Oznameni>
                    <Volby name="Cena zkušebního" value={stav.cena || ""} onChange={(v) => { nast("cena", v); setChyba(""); }} moznosti={[
                      { key: "1000", label: `${cenaPlna}, plná cena`, popis: "Už na zkušebním byla, nebo minule nepřišla bez omluvy" },
                      { key: "490", label: `${cenaNizka}, zvýhodněná cena`, popis: "Výjimka, napiš proč, uloží se do historie" },
                    ]} />
                    {stav.cena === "490" && (
                      <Field label="Proč zvýhodněná cena"><input className="field" value={stav.cenaProc || ""} onChange={(e) => nast("cenaProc", e.target.value)} placeholder="Například: minule byla nemocná, omluvila se" /></Field>
                    )}
                    {stav.cena === "1000" && (
                      <CoRict>Zvýhodněnou cenu {cenaNizka} máme jen pro úplně první zkušební trénink. Protože jste u nás už byla, tentokrát je trénink za běžnou cenu <b>{cenaPlna}</b>. Fakturu vám pošlu e‑mailem spolu s ostatními informacemi.</CoRict>
                    )}
                  </div>
                )}
                {preruseni}
              </div>
            )}

            {stav.krok === 5 && (
              <div className="space-y-4">
                <CoRict>Všechno vám tedy pošlu do e‑mailu a budeme se na vás moc těšit {t && (dowOf(t.datum) === 3 || dowOf(t.datum) === 4) ? "ve" : "v"} <b>{t ? `${DNY_7[dowOf(t.datum)]} ${dm(t.datum)} v ${casBezNuly(t.cas)}` : ""}</b>. Zkontrolujte si prosím i hromadnou poštu a spam.</CoRict>
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-1">
                    <RadekShrnuti popisek="Termín">{kdyText}, trvá 90 minut</RadekShrnuti>
                    <RadekShrnuti popisek="Trenér">{t?.trener}</RadekShrnuti>
                    {pravidelne && <RadekShrnuti popisek="Pravidelně">{pravidelne}</RadekShrnuti>}
                    {d.delka && <RadekShrnuti popisek="Délka">{d.delka}</RadekShrnuti>}
                    <RadekShrnuti popisek="E‑mail">{stav.email}</RadekShrnuti>
                    <RadekShrnuti popisek="Faktura">{f.typ === "firma" ? `na firmu ${f.firma}` : "na osobu"}{jinyTrener ? `, ${cenaNizka}, jiný trenér` : volbaCeny && stav.cena ? `, ${stav.cena === "1000" ? `${cenaPlna}, plná cena` : `${cenaNizka}, výjimka`}` : ""}</RadekShrnuti>
                  </div>
                  <div className="space-y-2 rounded-2xl border border-brand-200 bg-brand-50 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-brand-deep">Po potvrzení se samo</div>
                    <Fajfky polozky={[
                      <>zapíše trénink <b>{t?.trener}</b> do kalendáře</>,
                      <>pošle trenérovi zprávu do <b>Slacku</b> a pozvánku</>,
                      <>vystaví <b>fakturu</b> ve FAPI a pošle ji klientce e‑mailem</>,
                      <>pošle <b>e‑mail s informacemi a ceníkem</b></>,
                      <>den předem pošle <b>připomínku</b> a trenérovi připomene zprávu pro ni</>,
                      <>přepne kontakt na <b>zkušební trénink</b></>,
                    ]} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
